/**
 * Read-only viewer for an ingested source document, with provenance.
 *
 * Sources (`type='source'`) are raw inputs — transcripts/files — that feed the
 * ingestion pipeline. This pane shows the source body with a **line-number
 * gutter** (1-based **physical** lines, muted, non-selectable) so providers can
 * locate the line ranges cited in `[[src:<id>#L12-24]]` citations. Since the
 * grounded-extraction fix, those `#L` numbers are physical line numbers, so the
 * gutter and the citation agree by eye.
 *
 * When the provider clicks "Go to source" on a citation, {@link AppContent}
 * routes a {@link SourceRevealTarget} here: the cited span is scrolled into view
 * and its line rows flash ({@link useScrollToSourceLine}). The cited text is
 * re-verified against the current body ({@link resolveCitationSpan}); if the
 * source has been edited so the cited lines no longer match, a **stale banner**
 * is shown and the highlight re-anchors to wherever the cited text moved — or,
 * if it can no longer be found, nothing is highlighted and the original excerpt
 * is surfaced (fail loud, never silently point at the wrong line).
 *
 * When the body looks like HTML (the warehouse `FROZENENCOUNTERHTML` visit-note
 * rendering, D1/ADR-065), it renders via {@link SanitizedHtmlBody} instead — a
 * strict-allowlist DOMPurify sanitize, since warehouse clob content is
 * untrusted (no scripts, no event handlers, no inline style). While there is no
 * line-number gutter for an HTML body, citation-reveal highlight is supported
 * by scrolling to and flashing the matching element via snippet matching.
 *
 * Source content is PHI — it is shown but never logged.
 */
import React, { useMemo } from "react";
import type { BlueprintDocument, BlueprintVersion } from "../../types";
import {
  resolveCitationSpan,
  type SourceReveal,
} from "../blueprint/citationResolve";
import { useScrollToSourceLine } from "../../hooks/useScrollToSourceLine";
import { looksLikeHtml, sanitizeSourceHtml } from "./sanitizeSourceHtml";
import Markdown from "../blueprint/Markdown";

/** Props for {@link SourceViewer}. */
interface SourceViewerProps {
  /** The source document handle. */
  document: BlueprintDocument;
  /** The source's current committed version (its body), if any. */
  version: BlueprintVersion | null;
  /** True while an ingestion run is in flight. */
  ingesting?: boolean;
  /** Re-run the ingestion pipeline on this source (currently deprioritized/hidden). */
  onIngest?: () => void;
  /**
   * A pending citation reveal targeting THIS source, or null. When set, the
   * cited span is scrolled into view + flashed; a stale/unlocatable span shows
   * a banner.
   */
  reveal?: SourceReveal | null;
}

/**
 * The body of a source document, rendered as formatted Markdown prose.
 *
 * Renders cleanly formatted prose inside a container carrying
 * `data-testid="source-body-prose"`. Citation reveals can scroll to and flash
 * elements containing cited text.
 *
 * @param props - `body`: the raw source text.
 * @returns A scrollable card with formatted markdown prose.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function deriveSnippetFromLines(
  body: string,
  lines: number[],
): string | undefined {
  if (!body || lines.length === 0) return undefined;
  const bodyLines = body.split("\n");
  const sorted = [...lines].sort((a, b) => a - b);
  for (const lineNo of sorted) {
    if (lineNo >= 1 && lineNo <= bodyLines.length) {
      const raw = bodyLines[lineNo - 1].trim();
      const cleaned = raw
        .replace(/^#{1,6}\s+/, "")
        .replace(/^[-*]\s+/, "")
        .replace(/^\d+\.\s+/, "")
        .replace(/\[\[src:[^\]]+\]\]/g, "")
        .replace(/\[\[cite:[^\]]*\]\]/g, "")
        .replace(/\[\[inf:[^\]]*\]\]/g, "")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\*([^*]+)\*/g, "$1")
        .replace(/__([^_]+)__/g, "$1")
        .replace(/_([^_]+)_/g, "$1")
        .trim();
      if (cleaned !== "") return cleaned;
    }
  }
  return undefined;
}

function ProseBody({ body }: { body: string }): React.ReactElement {
  return (
    <div
      className="overflow-auto rounded-xl border border-slate-200 bg-white p-6 text-sm"
      data-testid="source-body-prose"
    >
      <div className="prose prose-slate max-w-none">
        <Markdown source={body} rawIdentifiers={true} />
      </div>
    </div>
  );
}

/**
 * A non-blocking banner shown when a cited span no longer matches the source.
 *
 * @param props - `unlocatable`: the cited text could not be found at all;
 *   `snippet`: the original cited text to surface when unlocatable.
 * @returns The stale-citation banner element.
 */
function StaleBanner({
  unlocatable,
  snippet,
}: {
  unlocatable: boolean;
  snippet: string;
}): React.ReactElement {
  return (
    <div
      data-testid="citation-stale-banner"
      className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      <p className="font-medium">
        This source has changed since this citation was made.
      </p>
      {unlocatable ? (
        <>
          <p className="mt-1">
            The cited text could not be located in the current source — showing
            the original excerpt:
          </p>
          {snippet.trim() !== "" && (
            <blockquote
              data-testid="citation-stale-excerpt"
              className="mt-1 border-l-2 border-amber-300 pl-2 italic whitespace-pre-wrap"
            >
              {snippet}
            </blockquote>
          )}
        </>
      ) : (
        <p className="mt-1">
          Highlighting the closest match to the cited text.
        </p>
      )}
    </div>
  );
}

/**
 * A sanitized-HTML rendering of a source body (D1, ADR-065).
 *
 * Warehouse HTML (`FROZENENCOUNTERHTML`, `onclick`/inline-style heavy) is
 * UNTRUSTED clob content. This is the only place a source body ever reaches
 * `dangerouslySetInnerHTML`, and only via {@link sanitizeSourceHtml}
 * (DOMPurify, strict allowlist — no scripts, no event handlers, no inline
 * style, no links/images). There is no line-number gutter here, but
 * citation reveal scrolls to and flash-highlights matching HTML elements via
 * verbatim snippet matching.
 *
 * @param props - `body`: the raw, unsanitized source text.
 * @returns A scrollable sanitized-HTML block.
 */
function SanitizedHtmlBody({ body }: { body: string }): React.ReactElement {
  const clean = useMemo(() => sanitizeSourceHtml(body), [body]);
  return (
    <div
      className="whitespace-pre-wrap overflow-auto rounded-xl border border-slate-200 bg-white p-4 text-sm"
      data-testid="source-body-html"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

/**
 * Render the source body (formatted Markdown prose) + provenance + the
 * Re-ingest action, scrolling to + flashing a cited span when one is revealed.
 *
 * @param props - The source document/version, busy flag, ingest handler, and an
 *   optional citation reveal target.
 * @returns The source-viewer element.
 */
export const SourceViewer: React.FC<SourceViewerProps> = ({
  document,
  version,
  reveal = null,
}) => {
  const body = version?.content.body ?? "";
  const isHtml = useMemo(() => looksLikeHtml(body), [body]);
  // Memoize on (reveal, body): stable across unrelated re-renders, but fresh on
  // each reveal (revealSource hands a new target object) so a repeat reveal of
  // the same span still re-fires the scroll + flash in the hook below.
  const resolved = useMemo(
    () =>
      reveal && body !== "" && !isHtml && reveal.target.lines.length > 0
        ? resolveCitationSpan(reveal.target.lines, reveal.target.snippet, body)
        : null,
    [reveal, body, isHtml],
  );
  const highlightLines = useMemo(() => resolved?.lines ?? [], [resolved]);
  const snippet = useMemo(() => {
    const raw = reveal?.target.snippet ?? undefined;
    if (raw && raw.trim() !== "") return raw;
    return deriveSnippetFromLines(body, highlightLines);
  }, [reveal?.target.snippet, body, highlightLines]);
  useScrollToSourceLine(highlightLines, snippet, reveal?.key);

  return (
    <div className="space-y-3" data-testid="source-viewer">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">{document.title}</h2>
      </div>

      <div className="text-[11px] text-slate-400 font-mono">
        {version ? (
          <>
            v{version.version_no} · {version.author}
            {version.source_ref ? ` · source: ${version.source_ref}` : ""}
          </>
        ) : (
          "no content yet"
        )}
      </div>

      {resolved?.stale && (
        <StaleBanner
          unlocatable={resolved.unlocatable}
          snippet={reveal?.target.snippet ?? ""}
        />
      )}

      {isHtml ? <SanitizedHtmlBody body={body} /> : <ProseBody body={body} />}
    </div>
  );
};

export default SourceViewer;
